import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { TabPanel } from '../../../../components/tab-panel';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';
import { UploadFileComponent } from './upload-file.component';
import { useFileDownload } from '../../../../components/file-download';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { useSaveData } from '../../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface DocumentRequestArgs {
  unitID?: number;
  tempEntityID?: any;
}

interface ProjectDocumentDialog {
  open: boolean;
  onClose: () => void;
  documentRequestArgs: DocumentRequestArgs;
}
export const UnitDocumentDialog = ({ open, onClose, documentRequestArgs }: ProjectDocumentDialog) => {
 
  const [value, setValue] = useState('create');
  const [fileUploadReqArgs, setFileUploadReqArgs] = useState(documentRequestArgs);
  const [newFiles, saveNewFiles] = useState<any[]>([]);
  const [editItem, setEditItem] = useState<any>();
  const { downloadFile, isLoading } = useFileDownload();

  const fileColumns: GridColumnConfig[] = [
    {
      title: 'File Name',
      field: 'fileName',
      filter: 'text',
      searchable: true,
    },
    {
      title: 'File Type',
      field: 'fileType',
      filter: 'text',
    },
    {
      title: 'Description',
      field: 'description',
      filter: 'text',
      searchable: true,
    },
    {
      title: 'File Type Name',
      field: 'fileTypeName',
      filter: 'text',
      searchable: true,
    },
    // {
    //   title: 'File Section',
    //   field: 'fileSection',
    //   filter: 'text',
    // },
    {
      title: 'Uploaded By',
      field: 'uploadedByFullName',
      filter: 'text',
      searchable: true,
    },
    {
      title: 'Uploaded On',
      field: 'uploadedOnText',
      filter: 'text',
      searchable: true,
    },
  ];

  const handleFileUploadCompleted = (value: any) => {
    setFileUploadReqArgs({ ...fileUploadReqArgs, tempEntityID: value.tempEntityID });
    setEditItem(undefined);
    setValue('view');
  };

  const handleEdit = (value: any) => {
    setEditItem(value);
    setValue('edit');
  };

  const handleClosed = () => {
    onClose();
  };

  const handleDownloadFile = async (data: any) => {
    downloadFile({ additionalData: data });
  };

  const handleRedirectToTab = (value: any) => {
    if (value == 'view') setEditItem(undefined);
    setValue(value);
  }

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('EditUnitFile', 'EditUnitFile')
  );

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredUnitFiles, setFilteredUnitFiles] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredUnitFileList = newFiles?.filter((unitFile) =>
      Object.values(unitFile).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredUnitFiles(filteredUnitFileList || []);
  }, [debouncedSearchTerm, newFiles]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'EditUnitFile',
      pageName: 'EditUnitFile',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'EditUnitFile',
      pageName: 'EditUnitFile',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------


  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={handleClosed}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 0, m: 0 }}>
        <Tabs value={value} onChange={(event: any, value: string) => handleRedirectToTab(value)}>
          <Tab value="view" label={<div style={styling.tabHeading}>FILE LIST</div>} />
          <Tab value="create" label={<div style={styling.tabHeading}>UPLOAD FILES</div>} hidden={!!editItem} />
          <Tab value="edit" label={<div style={styling.tabHeading}>EDIT UPLOAD FILE</div>} hidden={!editItem} />
        </Tabs>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <TabPanel selectedValue={value} value="view">
          <Grid container spacing={2}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <SiTrackGrid
                fileTitle='unit_file'
                dataItemKey="fileID"
                columns={fileColumns}
                queryConfig={fileUploadApiKeyStore.getFiles(documentRequestArgs)}
                onDownload={(data: any) => {
                  handleDownloadFile(data);
                }}
                actionPermissions={{
                  edit: UserPermissionName.EDIT_FILE,
                  delete: UserPermissionName.DELETE_FILE,
                  download: UserPermissionName.DOWNLOAD_FILE,
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                onEdit={handleEdit}
                deleteMutationConfig={(item) => fileUploadApiKeyStore.deleteFile(item.fileID)}
                isBusy={isLoading || isLoadingGridLayout}

                // -----------------Grid Layout-------
                dataItems={filteredUnitFiles || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Grid container justifyContent="flex-end">
                <Button variant="outlined" style={{ width: '130px' }} onClick={handleClosed}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel selectedValue={value} value="create">
          <UploadFileComponent
            documentRequestArgs={fileUploadReqArgs}
            onSaveComplete={handleFileUploadCompleted}
            onClose={handleClosed}
          />
        </TabPanel>
        <TabPanel selectedValue={value} value="edit">
          <UploadFileComponent
            fileData={editItem}
            documentRequestArgs={fileUploadReqArgs}
            onSaveComplete={handleFileUploadCompleted}
            onClose={handleClosed}
          />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};
